import React from 'react';

import type { JSX } from 'react';
import type {
  VinkComponent as Component,
  FactComponent,
  GalleryComponent,
  GradeComponent,
  ImageComponent,
  SubheadingComponent,
  TextComponent,
  UrlComponent,
  VideoComponent,
  AdComponent,
  EmbeddedComponent,
  VinkTeaserComponent,
  VinkBotComponent,
  PollComponent,
} from '../../../../../core/data-sources/content-components/types.js';

import TextWrapper from './Text/TextWrapper.js';
import Image from './Image.js';
import Gallery from './Gallery/Gallery.js';
import FactBox from './FactBox.js';
import InlineGrade from './InlineGrade.js';
import Subheading from './Subheading.js';
import UrlWrapper from './UrlWrapper.js';
import VideoAssetPlayer from './VideoAssetPlayer.js';
import LinkInternal from './LinkInternal.js';
import Ad from '../../ads/Ad/Ad.js';
import Integration from './Integration.js';
import Vinkomat from '../../vinkomat/index.js';
import Poll from './Poll/index.js';

interface ComponentSelectorProps {
  item: Component;
  index: number;
  articleTitle: string;
  isMobile: boolean;
  groupIndex: number;
  isGuide: boolean;
}

type ComponentType =
  | 'text'
  | 'url'
  | 'image'
  | 'teaser'
  | 'subheading'
  | 'gallery'
  | 'fact'
  | 'grade'
  | 'video'
  | 'ad'
  | 'integration-enrichment'
  | 'vink-bot'
  | 'poll'
  | 'default';

type ComponentMappingInterface = {
  [K in ComponentType]: (props: ComponentSelectorProps) => JSX.Element | null;
};

const isComponentType = <T extends Component>(
  component: Component,
  type: string,
): component is T => 'type' in component && component.type === type;

// TODO: clean up prop passing to components

const ComponentMapping: ComponentMappingInterface = {
  text: (props) =>
    isComponentType<TextComponent>(props.item, 'text') ? (
      <TextWrapper
        item={props.item}
        groupIndex={props.groupIndex}
        index={props.index}
      />
    ) : null,
  url: (props) =>
    isComponentType<UrlComponent>(props.item, 'url') ? (
      <UrlWrapper
        item={props.item}
        groupIndex={props.groupIndex}
        index={props.index}
        isMobile={props.isMobile}
      />
    ) : null,
  image: (props) =>
    isComponentType<ImageComponent>(props.item, 'image') ? (
      <Image
        {...props.item}
        isMobile={props.isMobile}
        articleTitle={props.articleTitle}
        lazyLoad
      />
    ) : null,
  teaser: (props) =>
    isComponentType<VinkTeaserComponent>(props.item, 'teaser') ? (
      <LinkInternal {...props.item} />
    ) : null,
  subheading: (props) =>
    isComponentType<SubheadingComponent>(props.item, 'subheading') ? (
      <Subheading item={props.item} />
    ) : null,
  gallery: (props) =>
    isComponentType<GalleryComponent>(props.item, 'gallery') ? (
      <Gallery
        item={props.item}
        groupIndex={props.groupIndex}
        isMobile={props.isMobile}
        articleTitle={props.articleTitle}
      />
    ) : null,
  fact: (props) =>
    isComponentType<FactComponent>(props.item, 'fact') ? (
      <FactBox {...props.item} />
    ) : null,
  grade: ({ item, groupIndex, isGuide }) =>
    (groupIndex || isGuide) &&
    isComponentType<GradeComponent>(item, 'grade') ? (
      <InlineGrade item={item} groupIndex={groupIndex} isGuide={isGuide} />
    ) : null,
  video: (props) =>
    isComponentType<VideoComponent>(props.item, 'video') ? (
      <VideoAssetPlayer
        video={props.item}
        isMobile={props.isMobile}
        isInListicle={!!props.groupIndex}
      />
    ) : null,
  ad: (props) =>
    isComponentType<AdComponent>(props.item, 'ad') ? (
      <Ad
        id={props.item.id}
        cssClass={props.item.cssClass}
        isMobile={props.isMobile}
      />
    ) : null,
  'integration-enrichment': (props) =>
    isComponentType<EmbeddedComponent>(props.item, 'integration-enrichment') ? (
      <Integration {...props.item} />
    ) : null,
  'vink-bot': (props) =>
    isComponentType<VinkBotComponent>(props.item, 'vink-bot') ? (
      <Vinkomat />
    ) : null,
  poll: (props) =>
    isComponentType<PollComponent>(props.item, 'poll') ? (
      <Poll {...props.item} />
    ) : null,
  default: () => null,
};

const ComponentSelector: React.FC<ComponentSelectorProps> = ({
  item,
  index,
  articleTitle,
  groupIndex,
  isGuide,
  isMobile = false,
}) => {
  const commonProps = {
    item,
    index,
    articleTitle,
    groupIndex,
    isGuide,
    isMobile,
  };
  const Component =
    ComponentMapping[item.type as ComponentType] || ComponentMapping['default'];

  return <Component {...commonProps} />;
};

export default ComponentSelector;
